/**
 * ConfigurarBeneficioAdminV2 - Employment Plan details for admins.
 * Design from Lovable PlanEmpleo.tsx, logic from chirigota.
 */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
	FileText,
	Shield,
	ExternalLink,
	Building2,
	Calendar,
	Percent,
	TrendingUp,
	PieChart,
	BarChart3,
	FileCheck,
	Download,
	Mail
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { PageHeader } from '../../../components/ui/page-header';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import ResponsiveTooltip from '../../../components/ui/responsive-tooltip';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from '../../../components/ui/accordion';
import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import getCompanyInfoEppConfig from '../../../api/companies/getCompanyInfoEppConfig';
import { CompanyWithEppConfigI } from '../../../api/companies/interfaces/CompanyInfo';
import {
	EmployerContributionTypeEnum,
	ContributionPeriodicity,
	CaserPlanSelected
} from '../../../types/enums';
import ProjectConfig from '../../../constants';
import { getEmploymentPlanDocs } from '../../../constants/employmentPlanDocs';

// ─── Static data for plan composition (replace with backend in future) ───

const LIFE_CYCLE_ALLOCATIONS = [
	{ age: 'Menor de 35 años', stocks: 80, bonds: 20 },
	{ age: 'Entre 35 y 44 años', stocks: 70, bonds: 30 },
	{ age: 'Entre 45 y 54 años', stocks: 60, bonds: 40 },
	{ age: 'Entre 55 y 64 años', stocks: 50, bonds: 50 },
	{ age: 'Mayor de 65 años', stocks: 40, bonds: 60 }
];

const INDEXA_ACCIONES_ETFS = [
	{ name: 'SPDR S&P 500 ETF', weight: 19.93, region: 'EEUU' },
	{ name: 'iShares Core S&P Total US Stock Market ETF', weight: 19.86, region: 'EEUU' },
	{ name: 'Amundi Stoxx Europe 600 UCITS ETF', weight: 14.92, region: 'Europa' },
	{ name: 'Vanguard Emerging Markets ETF', weight: 10.13, region: 'Emergentes' },
	{ name: 'Xtrackers Euro Stoxx 50 UCITS ETF', weight: 10.0, region: 'Europa' },
	{ name: 'Vanguard Total Stock Market ETF', weight: 9.95, region: 'EEUU' },
	{ name: 'Nomura TOPIX ETF', weight: 7.08, region: 'Japón' },
	{ name: 'Vanguard FTSE Developed ETF', weight: 5.0, region: 'Desarrollados' }
];

const INDEXA_BONOS_ETFS = [
	{ name: 'Vanguard Total Bond Market ETF', weight: 20.03, region: 'Global' },
	{ name: 'iShares Global Aggregate EUR-H ACC ETF', weight: 20.02, region: 'Global' },
	{ name: 'SPDR BBG Barclays Global Aggregate EUR HDG ETF', weight: 15.03, region: 'Global' },
	{ name: 'Amundi Euro Government Bond UCITS ETF', weight: 10.03, region: 'Europa' },
	{ name: 'Amundi Prime Euro Government Bond UCITS ETF', weight: 10.02, region: 'Europa' }
];

// TODO: Habilitar cuando tengamos API de rentabilidades reales
const SHOW_HISTORICAL_RETURNS = false;

// Historical returns - SENTINEL: replace with real data from backend
const HISTORICAL_RETURNS = {
	indexa_acciones: [
		{ label: '2025 (YTD)', value: 77.7 },
		{ label: '2024', value: 77.7 },
		{ label: '2023', value: 77.7 },
		{ label: '2022', value: 77.7 },
		{ label: 'Desde inicio', value: 77.7 }
	],
	ciclos_vida: [
		{ label: '2025 (YTD)', value: 77.7 },
		{ label: '2024', value: 77.7 },
		{ label: '2023', value: 77.7 },
		{ label: '2022', value: 77.7 },
		{ label: 'Desde inicio', value: 77.7 }
	]
};

// Plan costs - Real public data from Caser/Indexa boletín (gestión 0,36% + depósito 0,05%)
const PLAN_COSTS = {
	indexa_acciones: { ter: 0.41, management: 0.36, custody: 0.05, sectorAvg: 1.25 },
	ciclos_vida: { ter: 0.41, management: 0.36, custody: 0.05, sectorAvg: 1.25 }
};

const fadeUp = (delay = 0) => ({
	initial: { opacity: 0, y: 12 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.35, delay }
});

const ConfigurarBeneficioAdminV2: React.FC = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, companyId } = useContext(ArcaContext);

	const [isLoading, setIsLoading] = useState(true);
	const [companyEppInfo, setCompanyEppInfo] = useState<CompanyWithEppConfigI | null>(null);

	useEffect(() => {
		const fetchData = async () => {
			if (!token || !companyId) return;

			try {
				const res = await getCompanyInfoEppConfig(token, companyId);
				const company =
					res.data?.allCompanies?.find((c) => c.id === companyId) || res.data?.allCompanies?.[0];
				if (company) setCompanyEppInfo(company);
			} catch (error) {
				console.error('Error fetching company info:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [token, companyId]);

	const caserPlanSelected = companyEppInfo?.companyEppConfig?.caserPlanSelected;

	const haveLifeCyclesPlan = caserPlanSelected === CaserPlanSelected.EMPLOYMENT_LIFE_CYCLES;

	const planDocs = useMemo(() => getEmploymentPlanDocs(caserPlanSelected), [caserPlanSelected]);

	const planType = haveLifeCyclesPlan ? 'ciclos_vida' : 'indexa_acciones';
	const returns = HISTORICAL_RETURNS[planType] || HISTORICAL_RETURNS.indexa_acciones;
	const costs = PLAN_COSTS[planType] || PLAN_COSTS.indexa_acciones;

	const costSaving = costs.sectorAvg - costs.ter;
	const costSavingPct = Math.round((costSaving / costs.sectorAvg) * 100);

	const getPeriodicityLabel = (periodicity: ContributionPeriodicity | undefined): string => {
		const mapping: Record<ContributionPeriodicity, string> = {
			[ContributionPeriodicity.MONTHLY]: translate('onboarding.employee.condition2.2'),
			[ContributionPeriodicity.BI_MONTHLY]: translate('onboarding.employee.condition2.3'),
			[ContributionPeriodicity.QUARTERLY]: translate('onboarding.employee.condition2.4'),
			[ContributionPeriodicity.SEMI_ANNUALLY]: translate('onboarding.employee.condition2.5'),
			[ContributionPeriodicity.YEARLY]: translate('onboarding.employee.condition2.6')
		};
		return periodicity ? mapping[periodicity] || '' : '';
	};

	const getContributionValue = (): string => {
		const config = companyEppInfo?.companyEppConfig;
		if (!config) return '—';

		if (config.seniorityBracket?.tiers && config.seniorityBracket.tiers.length > 0) {
			return translate('admin.planEmpleo.variableByTier');
		}

		if (config.employerContributionType === EmployerContributionTypeEnum.GROSS_SALARY_PERCENTAGE) {
			return `${config.employerContributionAmount}%`;
		}
		return `${config.employerContributionAmount}€/mes`;
	};

	const getContributionTypeLabel = (): string => {
		const config = companyEppInfo?.companyEppConfig;
		if (!config) return '—';

		if (config.employerContributionType === EmployerContributionTypeEnum.GROSS_SALARY_PERCENTAGE) {
			return translate('admin.planEmpleo.percentageSalary');
		}
		return translate('admin.planEmpleo.fixedAmount');
	};

	const formatPlanStartDate = (): string => {
		// TODO: Backend should provide planStartDate in companyEppConfig
		// Using createdAt as proxy for now
		const date = companyEppInfo?.companyEppConfig?.createdAt;
		if (!date) return '—';
		return new Date(date).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
	};

	const formatPlanStartDateLong = (): string => {
		// TODO: Backend should provide planStartDate in companyEppConfig
		// Using createdAt as proxy for now
		const date = companyEppInfo?.companyEppConfig?.createdAt;
		if (!date) return '—';
		return new Date(date).toLocaleDateString('es-ES', {
			day: 'numeric',
			month: 'long',
			year: 'numeric'
		});
	};

	// Admin-specific signed documents
	const adminDocs = [
		{
			title: translate('admin.planEmpleo.adhesionAct'),
			subtitle: translate('admin.planEmpleo.signedByCompany'),
			status: 'firmado'
		},
		{
			title: translate('admin.planEmpleo.planSpecs'),
			subtitle: translate('admin.planEmpleo.agreedConditions'),
			status: 'firmado'
		},
		{
			title: translate('admin.planEmpleo.planRegulation'),
			subtitle: translate('admin.planEmpleo.internalRules'),
			status: 'firmado'
		}
	];

	// Plan documents for download
	const planDocuments = useMemo(() => {
		const docs = [];

		// Datos Fundamentales del fondo de Acciones. Sin URL no se pinta: es
		// preferible no mostrar nada a entregar el documento de otro plan.
		if (planDocs.dfpStocksUrl) {
			docs.push({
				title: translate('user.employmentPlan.actionsFundamentalData'),
				subtitle: translate('admin.planEmpleo.indexaEmpleoAcciones'),
				url: planDocs.dfpStocksUrl
			});
		}

		// Datos Fundamentales del fondo de Bonos: solo en los planes que lo tienen
		if (planDocs.dfpBondsUrl) {
			docs.push({
				title: translate('user.employmentPlan.boundsFundamentalData'),
				subtitle: translate('admin.planEmpleo.indexaEmpleoBonos'),
				url: planDocs.dfpBondsUrl
			});
		}

		// Anexo al plan de empleo
		if (companyEppInfo?.companyEppConfig?.linkAnnex) {
			docs.push({
				title: translate('admin.planEmpleo.annexDocument'),
				subtitle: translate('admin.planEmpleo.annexDescription'),
				url: companyEppInfo.companyEppConfig.linkAnnex
			});
		}

		return docs;
	}, [planDocs, companyEppInfo, translate]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
			</div>
		);
	}

	return (
		<div className="max-w-6xl mx-auto space-y-8">
			{/* Cabecera — la pone `ui/PageHeader`, puerto de Proa v0.10.0.
			    Esta pantalla NO tenía título propio: su único `h1` era el nombre del producto
			    («Indexa Empleo Ciclo de Vida») dentro de la tarjeta de abajo, así que se llamaba
			    «Plan de Empleo» en el menú y en ninguna parte de la página. Ese `h1` baja a `h2`,
			    que es lo que siempre fue: el título de una tarjeta.
			    El título sale de la MISMA clave que la etiqueta del menú, a propósito: quien llega
			    tiene que poder confirmar dónde está sin traducir nombres.
			    `width="full"` porque el ancho ya lo pone el contenedor de la pantalla
			    (`max-w-6xl`, justo arriba). */}
			<motion.div {...fadeUp()}>
				<PageHeader
					width="full"
					title={translate('admin.dashboard.setBenefit')}
					subtitle={translate('admin.planEmpleo.screenSubtitle')}
				/>
			</motion.div>

			{/* ─── 1. HERO ─── */}
			<motion.div {...fadeUp(0.06)}>
				<Card className="border-2 border-primary/20 overflow-hidden">
					<CardContent className="p-0">
						<div className="bg-primary/5 border-b border-primary/10 px-6 py-4">
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
								<div>
									<div className="flex items-center gap-2 mb-1">
										<h2 className="text-xl font-bold text-foreground">
											{haveLifeCyclesPlan
												? translate('admin.planEmpleo.indexaEmpleoCicloVida')
												: translate('admin.planEmpleo.indexaEmpleo100Acciones')}
										</h2>
										<Badge variant="default" className="text-[10px] uppercase tracking-wider">
											{translate('admin.planEmpleo.indexedManagement')}
										</Badge>
									</div>
									<p className="text-sm text-muted-foreground">
										{haveLifeCyclesPlan
											? translate('admin.planEmpleo.lifeCycleStrategy')
											: translate('admin.planEmpleo.stocksStrategy')}
									</p>
								</div>
								<Badge
									variant="outline"
									className="self-start text-xs border-primary/30 text-primary gap-1"
								>
									<Shield className="w-3 h-3" />
									{translate('admin.planEmpleo.ppeBadge')}
								</Badge>
							</div>
						</div>

						<div
							className={`grid grid-cols-2 ${SHOW_HISTORICAL_RETURNS ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} divide-x divide-border`}
						>
							{[
								...(SHOW_HISTORICAL_RETURNS
									? [
											{
												label: translate('admin.planEmpleo.ytdReturn'),
												value: `${returns[0]?.value > 0 ? '+' : ''}${returns[0]?.value ?? 0}%`,
												icon: <TrendingUp className="w-4 h-4" />,
												positive: (returns[0]?.value ?? 0) > 0,
												tip: translate('admin.planEmpleo.ytdReturnTip')
											}
										]
									: []),
								{
									label: translate('admin.planEmpleo.totalCostTer'),
									value: `${costs.ter.toFixed(2)}%`,
									icon: <Percent className="w-4 h-4" />,
									positive: true,
									tip: translate('admin.planEmpleo.terTip')
								},
								{
									label: translate('admin.planEmpleo.companyContribution'),
									value: getContributionValue(),
									icon: <Building2 className="w-4 h-4" />,
									positive: true,
									tip: translate('admin.planEmpleo.contributionTip')
								},
								{
									label: translate('admin.planEmpleo.planStart'),
									value: formatPlanStartDate(),
									icon: <Calendar className="w-4 h-4" />,
									positive: true,
									tip: translate('admin.planEmpleo.planStartTip')
								}
							].map((kpi) => (
								<div key={kpi.label} className="p-4 text-center">
									<ResponsiveTooltip content={kpi.tip} side="bottom">
										<div className="cursor-help">
											<div className="flex items-center justify-center gap-1 mb-1">
												<span className="text-muted-foreground">{kpi.icon}</span>
												<p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
													{kpi.label}
												</p>
											</div>
											<p
												className={`text-lg font-bold ${kpi.positive ? 'text-primary' : 'text-destructive'}`}
											>
												{kpi.value}
											</p>
										</div>
									</ResponsiveTooltip>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			</motion.div>

			{/* ─── 2. CONDICIONES DEL PLAN ─── */}
			<motion.div {...fadeUp(0.06)}>
				<div className="flex items-center gap-2 mb-4">
					<FileCheck className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">{translate('admin.planEmpleo.planConditions')}</h2>
				</div>
				<Card className="border-none shadow-xs">
					<CardContent className="p-5">
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground uppercase tracking-wider">
									{translate('admin.planEmpleo.contributionType')}
								</p>
								<p className="text-sm font-semibold text-foreground">
									{getContributionTypeLabel()}
								</p>
							</div>
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground uppercase tracking-wider">
									{translate('admin.planEmpleo.amountPercentage')}
								</p>
								<p className="text-sm font-semibold text-foreground">{getContributionValue()}</p>
							</div>
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground uppercase tracking-wider">
									{translate('admin.planEmpleo.periodicity')}
								</p>
								<p className="text-sm font-semibold text-foreground">
									{getPeriodicityLabel(
										companyEppInfo?.companyEppConfig
											?.contributionPeriodicity as ContributionPeriodicity
									)}
								</p>
							</div>
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground uppercase tracking-wider">
									{translate('admin.planEmpleo.employeeContribution')}
								</p>
								<p className="text-sm font-semibold text-foreground">
									{companyEppInfo?.companyEppConfig?.employeeContributionRequired === false
										? translate('admin.planEmpleo.voluntaryNotMandatory')
										: `${translate('admin.planEmpleo.mandatoryMin')} ${companyEppInfo?.companyEppConfig?.employeeContributionMinimum || 0}€/mes`}
								</p>
							</div>
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground uppercase tracking-wider">
									{translate('admin.planEmpleo.startDate')}
								</p>
								<p className="text-sm font-semibold text-foreground">{formatPlanStartDateLong()}</p>
							</div>
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground uppercase tracking-wider">
									{translate('admin.planEmpleo.company')}
								</p>
								<p className="text-sm font-semibold text-foreground">
									{companyEppInfo?.name || '—'}
								</p>
							</div>
						</div>
						<p className="text-xs text-muted-foreground mt-5 pt-4 border-t border-border">
							{translate('admin.planEmpleo.modifyConditions')}{' '}
							<a
								href={`mailto:${ProjectConfig.ARCA_CONTACT.EMAIL}`}
								className="text-primary hover:underline font-medium"
							>
								{ProjectConfig.ARCA_CONTACT.EMAIL}
							</a>
						</p>
					</CardContent>
				</Card>
			</motion.div>

			{/* ─── 3. DOCUMENTACIÓN FIRMADA ─── */}
			<motion.div {...fadeUp(0.1)}>
				<div className="flex items-center gap-2 mb-4">
					<FileCheck className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">{translate('admin.planEmpleo.signedDocs')}</h2>
					<ResponsiveTooltip content={translate('admin.planEmpleo.signedDocsTip')} side="top" />
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
					{adminDocs.map((doc, idx) => (
						<Card key={idx} className="border shadow-xs">
							<CardContent className="p-4 flex items-center gap-4">
								<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
									<FileCheck className="w-5 h-5 text-primary" />
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{doc.title}</p>
									<p className="text-xs text-muted-foreground">{doc.subtitle}</p>
								</div>
								<Badge className="bg-primary/10 text-primary border-0 text-[10px] uppercase tracking-wider shrink-0">
									✓ {translate('admin.planEmpleo.signed')}
								</Badge>
							</CardContent>
						</Card>
					))}
				</div>
			</motion.div>

			{/* ─── 4. RENTABILIDAD HISTÓRICA ─── */}
			{SHOW_HISTORICAL_RETURNS && (
				<motion.div {...fadeUp(0.14)}>
					<div className="flex items-center gap-2 mb-4">
						<BarChart3 className="w-5 h-5 text-primary" />
						<h2 className="text-lg font-semibold">
							{translate('admin.planEmpleo.historicalReturns')}
						</h2>
						<ResponsiveTooltip
							content={translate('admin.planEmpleo.historicalReturnsTip')}
							side="top"
						/>
					</div>
					<div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
						{returns.map((r) => (
							<Card key={r.label} className="border-none shadow-xs">
								<CardContent className="p-4 text-center">
									<p className="text-xs text-muted-foreground mb-1">{r.label}</p>
									<p
										className={`text-2xl font-bold tracking-tight ${r.value >= 0 ? 'text-primary' : 'text-destructive'}`}
									>
										{r.value > 0 ? '+' : ''}
										{r.value}%
									</p>
								</CardContent>
							</Card>
						))}
					</div>
					<p className="text-[11px] text-muted-foreground mt-2 italic">
						* {translate('admin.planEmpleo.pastReturnsDisclaimer')}
					</p>
				</motion.div>
			)}

			{/* ─── 5. COMISIONES Y COSTES ─── */}
			<motion.div {...fadeUp(0.18)}>
				<div className="flex items-center gap-2 mb-4">
					<Percent className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">{translate('admin.planEmpleo.feesAndCosts')}</h2>
				</div>
				<Card className="border-none shadow-xs">
					<CardContent className="p-5">
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
							<div>
								<p className="text-xs text-muted-foreground mb-1">
									{translate('admin.planEmpleo.managementFee')}
								</p>
								<p className="text-2xl font-bold text-foreground">{costs.management.toFixed(2)}%</p>
								<p className="text-[11px] text-muted-foreground">
									{translate('admin.planEmpleo.annualOnAssets')}
								</p>
							</div>
							<div>
								<p className="text-xs text-muted-foreground mb-1">
									{translate('admin.planEmpleo.custodyFee')}
								</p>
								<p className="text-2xl font-bold text-foreground">{costs.custody.toFixed(3)}%</p>
								<p className="text-[11px] text-muted-foreground">
									{translate('admin.planEmpleo.depositary')}
								</p>
							</div>
							<div>
								<p className="text-xs text-muted-foreground mb-1">
									{translate('admin.planEmpleo.totalCostTer')}
								</p>
								<p className="text-2xl font-bold text-primary">{costs.ter.toFixed(2)}%</p>
								<p className="text-[11px] text-muted-foreground">
									{translate('admin.planEmpleo.allIncluded')}
								</p>
							</div>
						</div>

						<div className="rounded-xl bg-primary/5 border border-primary/10 p-4">
							<div className="flex items-center justify-between mb-3">
								<p className="text-sm font-medium text-foreground">
									{translate('admin.planEmpleo.sectorComparison')}
								</p>
								<Badge className="bg-primary text-primary-foreground text-xs">
									{costSavingPct}% {translate('admin.planEmpleo.cheaper')}
								</Badge>
							</div>
							<div className="space-y-3">
								<div>
									<div className="flex justify-between text-xs mb-1">
										<span className="text-primary font-medium">
											{translate('admin.planEmpleo.yourPlan')} — {costs.ter.toFixed(2)}%
										</span>
									</div>
									<Progress value={(costs.ter / costs.sectorAvg) * 100} className="h-2.5" />
								</div>
								<div>
									<div className="flex justify-between text-xs mb-1">
										<span className="text-muted-foreground">
											{translate('admin.planEmpleo.sectorAverage')} — {costs.sectorAvg.toFixed(2)}%
										</span>
									</div>
									<Progress value={100} className="h-2.5 [&>div]:bg-muted-foreground/30" />
								</div>
							</div>
							<p className="text-xs text-muted-foreground mt-3">
								{translate('admin.planEmpleo.estimatedSaving')}{' '}
								<span className="font-semibold text-primary">
									{costSaving.toFixed(2)} {translate('admin.planEmpleo.percentagePoints')}
								</span>{' '}
								{translate('admin.planEmpleo.annualVsSector')}
							</p>
						</div>
					</CardContent>
				</Card>
			</motion.div>

			{/* ─── 6. COMPOSICIÓN DE LA CARTERA ─── */}
			<motion.div {...fadeUp(0.22)}>
				<div className="flex items-center gap-2 mb-4">
					<PieChart className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">
						{translate('admin.planEmpleo.portfolioComposition')}
					</h2>
				</div>

				{haveLifeCyclesPlan && (
					<Card className="border-2 border-primary/20 mb-4">
						<CardContent className="p-5">
							<div className="flex items-center gap-2 mb-3">
								<TrendingUp className="w-4 h-4 text-primary" />
								<p className="text-sm font-semibold">
									{translate('admin.planEmpleo.ageDistribution')}
								</p>
							</div>
							<p className="text-xs text-muted-foreground mb-4">
								{translate('admin.planEmpleo.ageDistributionDesc')}
							</p>
							<div className="overflow-x-auto">
								<table className="w-full text-sm">
									<thead>
										<tr className="border-b">
											<th className="text-left py-2 px-3 font-medium text-muted-foreground">
												{translate('admin.planEmpleo.ageBracket')}
											</th>
											<th className="text-center py-2 px-3 font-medium text-primary">
												{translate('admin.planEmpleo.stocks')}
											</th>
											<th className="text-center py-2 px-3 font-medium text-muted-foreground">
												{translate('admin.planEmpleo.bonds')}
											</th>
										</tr>
									</thead>
									<tbody>
										{LIFE_CYCLE_ALLOCATIONS.map((alloc, idx) => (
											<tr key={idx} className="border-b last:border-0">
												<td className="py-2 px-3 text-sm">{alloc.age}</td>
												<td className="text-center py-2 px-3 font-medium">{alloc.stocks}%</td>
												<td className="text-center py-2 px-3 font-medium">{alloc.bonds}%</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</CardContent>
					</Card>
				)}

				<Accordion
					type={haveLifeCyclesPlan ? 'multiple' : 'single'}
					collapsible={!haveLifeCyclesPlan}
					className="w-full space-y-3"
				>
					{haveLifeCyclesPlan ? (
						<>
							<AccordionItem value="acciones" className="border rounded-xl overflow-hidden">
								<AccordionTrigger className="bg-primary text-primary-foreground px-4 py-3 hover:no-underline text-sm font-medium">
									{translate('admin.planEmpleo.indexaEmpleoAcciones')}
								</AccordionTrigger>
								<AccordionContent className="p-4">
									<HoldingsList items={INDEXA_ACCIONES_ETFS} colorClass="text-primary" />
								</AccordionContent>
							</AccordionItem>
							<AccordionItem value="bonos" className="border rounded-xl overflow-hidden">
								<AccordionTrigger className="bg-muted-foreground/80 text-white px-4 py-3 hover:no-underline text-sm font-medium">
									{translate('admin.planEmpleo.indexaEmpleoBonos')}
								</AccordionTrigger>
								<AccordionContent className="p-4">
									<HoldingsList items={INDEXA_BONOS_ETFS} colorClass="text-muted-foreground" />
								</AccordionContent>
							</AccordionItem>
						</>
					) : (
						<AccordionItem value="holdings" className="border rounded-xl overflow-hidden">
							<AccordionTrigger className="bg-primary text-primary-foreground px-4 py-3 hover:no-underline text-sm font-medium">
								{translate('admin.planEmpleo.mainPositions')}
							</AccordionTrigger>
							<AccordionContent className="p-4">
								<HoldingsList items={INDEXA_ACCIONES_ETFS} colorClass="text-primary" />
							</AccordionContent>
						</AccordionItem>
					)}
				</Accordion>
			</motion.div>

			{/* ─── 7. DOCUMENTACIÓN DEL PLAN ─── */}
			<motion.div {...fadeUp(0.26)}>
				<div className="flex items-center gap-2 mb-4">
					<FileText className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">
						{translate('admin.planEmpleo.planDocumentation')}
					</h2>
				</div>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
					{planDocuments.map((doc, idx) => (
						<Card key={idx} className="border shadow-xs hover:border-primary/30 transition-colors">
							<CardContent className="p-4 flex items-center gap-4">
								<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
									<FileText className="w-5 h-5 text-primary" />
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium truncate">{doc.title}</p>
									<p className="text-xs text-muted-foreground">{doc.subtitle}</p>
								</div>
								<a href={doc.url} target="_blank" rel="noopener noreferrer">
									<Button
										size="icon"
										variant="ghost"
										className="shrink-0 text-primary hover:bg-primary/10"
									>
										<Download className="w-4 h-4" />
									</Button>
								</a>
							</CardContent>
						</Card>
					))}
				</div>
			</motion.div>

			{/* ─── 8. ENTIDADES DEL PLAN ─── */}
			<motion.div {...fadeUp(0.3)}>
				<Card className="border-none shadow-xs">
					<CardContent className="p-5">
						<h2 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wider">
							{translate('admin.planEmpleo.planEntities')}
						</h2>
						<div className="flex flex-wrap items-center gap-6 mb-4">
							<img
								src="/imgs/caser-helvetia.png"
								alt="Caser Pensiones"
								className="h-8 object-contain"
							/>
							<img
								src="/imgs/indexa-capital.png"
								alt="Indexa Capital"
								className="h-8 object-contain"
							/>
							<img src="/imgs/cecabank-logo.png" alt="Cecabank" className="h-8 object-contain" />
						</div>
						<p className="text-sm text-muted-foreground mb-3">
							{translate('admin.planEmpleo.entitiesDescription')}
						</p>

						<div className="flex flex-wrap gap-2 mb-3">
							<Badge variant="outline" className="text-xs text-muted-foreground">
								{translate('admin.planEmpleo.stocks')}: Fondo {planDocs.stocksFundCode} · Plan{' '}
								{planDocs.dgsfpCode}
							</Badge>
							{planDocs.bondsFundCode && (
								<Badge variant="outline" className="text-xs text-muted-foreground">
									{translate('admin.planEmpleo.bonds')}: Fondo {planDocs.bondsFundCode} · Plan{' '}
									{planDocs.dgsfpCode}
								</Badge>
							)}
						</div>
						<a
							href="https://dgsfp.mineco.gob.es/"
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
						>
							{translate('admin.planEmpleo.consultDgsfp')}
							<ExternalLink className="w-3 h-3" />
						</a>
					</CardContent>
				</Card>
			</motion.div>

			{/* ─── 9. SOPORTE ─── */}
			<motion.div {...fadeUp(0.34)}>
				<Card className="border border-border shadow-xs">
					<CardContent className="p-5">
						<div className="flex items-start gap-4">
							<div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
								<Mail className="w-5 h-5 text-primary" />
							</div>
							<div>
								<h3 className="text-sm font-semibold text-foreground mb-1">
									{translate('admin.planEmpleo.needHelp')}
								</h3>
								<p className="text-sm text-muted-foreground mb-2">
									{translate('admin.planEmpleo.helpDescription')}
								</p>
								<a
									href={`mailto:${ProjectConfig.ARCA_CONTACT.EMAIL}`}
									className="inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium"
								>
									{ProjectConfig.ARCA_CONTACT.EMAIL}
									<ExternalLink className="w-3 h-3" />
								</a>
							</div>
						</div>
					</CardContent>
				</Card>
			</motion.div>
		</div>
	);
};

/** Reusable holdings list */
const HoldingsList = ({
	items,
	colorClass
}: {
	items: { name: string; weight: number; region: string }[];
	colorClass: string;
}) => (
	<div className="space-y-1">
		{items.map((item, idx) => (
			<div
				key={idx}
				className="flex justify-between items-center py-2 border-b border-border/50 last:border-0"
			>
				<div className="flex items-center gap-2 min-w-0">
					<span className="text-sm font-medium truncate">{item.name}</span>
					<Badge variant="outline" className="text-[10px] shrink-0">
						{item.region}
					</Badge>
				</div>
				<span className={`text-sm font-bold ${colorClass} shrink-0 ml-2`}>{item.weight}%</span>
			</div>
		))}
	</div>
);

export default ConfigurarBeneficioAdminV2;
